// Pure, framework-free helpers for computing per-item, discount- and
// prior-payment-aware net dues on a pickup bill.
//
// Extracted out of PickupModal.tsx (rather than duplicated) so that:
//  - the live pickup-selection screen (dueForSelectedItems/amountDue), and
//  - the itemized partial-pickup receipt (PickupReceiptModal, built from
//    PickupModal's handlePickupSubmit)
// always reconcile to the exact same numbers, and so this math is
// unit-testable in isolation without rendering the whole modal.
//
// Deliberately typed against minimal, structural (duck-typed) shapes rather
// than PickupModal's own IPickupCartItem/IPickupCartBill so this module has
// no dependency on that component - any object with these fields (which
// IPickupCartItem/IPickupCartBill are supersets of) satisfies them.

export interface INetDueCartItem {
  itemId: string;
  selected: boolean;
  qty: number;
  unitPrice: number;
  alterationItems: { unitPrice?: number | null }[];
  upcharges: { subUpcharge: { price: number } }[];
  spots: { subSpot: { price?: number | null } }[];
  // Special ticket charge saved on the item at check-in
  specialCharge?: number;
  // Pieces of this line already handed over, and pieces selected to leave in
  // this transaction. Absent for callers that predate partial-quantity pickup,
  // where a line was collected whole - `releasedFractionOf` then treats a
  // selected line as fully released and an unselected one as not released.
  pickedQty?: number;
  selectedQty?: number;
}

// Matches the gross-total components the backend's pro-rata discount split
// sums per item (itemPrice*qty + alterationTotal + its own upcharges + its
// own spot charges + its Special charge) - see BillItemPricingCalculator
// on the backend.
export const itemTotal = (item: INetDueCartItem): number =>
  item.qty * item.unitPrice +
  item.alterationItems.reduce((acc, curr) => acc + (curr?.unitPrice ?? 0), 0) +
  item.upcharges.reduce((acc, curr) => acc + curr.subUpcharge.price, 0) +
  item.spots.reduce((acc, curr) => acc + (curr.subSpot.price ?? 0), 0) +
  (item.specialCharge ?? 0);

// Splits `total` across `weights` proportionally, rounded to the cent, with
// any leftover cent(s) from rounding absorbed by the last weight so the
// shares always sum EXACTLY to `total` - mirrors the backend's pro-rata
// discount apportionment (BillServiceImpl.getItemDiscountShares) so the FE's
// own per-item split stays consistent with it.
export const apportion = (total: number, weights: number[]): number[] => {
  const sumWeights = weights.reduce((acc, w) => acc + w, 0);
  if (!weights.length) return [];
  if (sumWeights <= 0 || total === 0) return weights.map(() => 0);

  const raw = weights.map(
    (w) => Math.round(((total * w) / sumWeights) * 100) / 100
  );
  const roundedSum = raw.reduce((acc, v) => acc + v, 0);
  const diff = Math.round((total - roundedSum) * 100) / 100;
  if (diff !== 0) {
    raw[raw.length - 1] = Math.round((raw[raw.length - 1] + diff) * 100) / 100;
  }
  return raw;
};

export interface INetDueResult {
  itemId: string;
  selected: boolean;
  netDue: number;
}

// Full per-item breakdown behind computeItemNetDues (see below) - exposes
// the intermediate grossTotal/discountShare/paidAmount components as well
// as the final netDue, so consumers that need a selection-scoped SUBTOTAL,
// DISCOUNT or PAID figure (not just the net due) can sum the same
// already-computed per-item numbers instead of re-deriving them from
// scratch. There is deliberately only ONE place (this function) that does
// the gross/discount-share/paid-share apportionment - computeItemNetDues is
// just a thin projection of it down to {itemId, selected, netDue}.
export interface INetDueBreakdown {
  itemId: string;
  selected: boolean;
  // This item's pre-discount gross total (itemTotal(item)).
  grossTotal: number;
  // This item's pro-rata share of the bill's effective discount.
  discountShare: number;
  // The share of the bill's credit (everything paid on it) this item uses
  // up - see allocateCredit.
  paidAmount: number;
  // grossTotal - discountShare - paidAmount, floored at 0.
  netDue: number;
}

// Everything paid on a bill is one pool of credit, used up by its garments
// in the order they leave the shop: first every piece already collected or
// selected to leave now (in bill order), then the pieces still in storage
// (in bill order). A payment is never tied to the item it happened to be
// recorded against - a deposit taken at drop-off, a payment made for one
// shirt at an earlier pickup and today's payment all come off the same pool.
//
// The pool used to be split pro-rata across items instead (item-scoped
// payments to their own items, the rest by remaining balance) and re-split
// on every visit, so the same deposit landed on different garments each
// time: odd per-visit amounts, and a garment collected and paid for on an
// earlier visit could show money owing again.
//
// The amount to collect for a pickup therefore always comes out as
//   (value of everything released, including today) - (everything paid)
// and never below zero - the same rule the backend's pickup guard applies.
interface ICreditAllocation {
  grossTotal: number;
  discountShare: number;
  releasedFraction: number;
  releasedValue: number;
  remainingValue: number;
  releasedCredit: number;
  remainingCredit: number;
}

const allocateCredit = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): ICreditAllocation[] => {
  const grossTotals = items.map((ci) => itemTotal(ci));
  const discountShares = apportion(effectiveDiscount, grossTotals);
  // bill.paidAmount already includes item-scoped payments; the per-item
  // figures only matter if they ever run ahead of it.
  const itemPaidTotal = items.reduce(
    (acc, ci) => acc + itemPaidAmountFor(ci.itemId),
    0
  );
  let credit = round2(Math.max(billPaidAmount, itemPaidTotal));

  const allocations = items.map((ci, idx) => {
    const linePrice = grossTotals[idx] - discountShares[idx];
    const releasedFraction = releasedFractionOf(ci);
    const releasedValue = round2(linePrice * releasedFraction);
    return {
      grossTotal: grossTotals[idx],
      discountShare: discountShares[idx],
      releasedFraction,
      releasedValue,
      remainingValue: round2(linePrice - releasedValue),
      releasedCredit: 0,
      remainingCredit: 0,
    };
  });

  const take = (amount: number) => {
    const taken = round2(Math.min(credit, Math.max(0, amount)));
    credit = round2(credit - taken);
    return taken;
  };
  allocations.forEach((a) => {
    a.releasedCredit = take(a.releasedValue);
  });
  allocations.forEach((a) => {
    a.remainingCredit = take(a.remainingValue);
  });
  return allocations;
};

// Per-item breakdown for every item on a bill: item.grossTotal minus its
// pro-rata discountShare (mirroring the backend's split) minus the share of
// the bill's credit it uses up (see allocateCredit).
export const computeItemBreakdown = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): INetDueBreakdown[] =>
  allocateCredit(items, effectiveDiscount, billPaidAmount, itemPaidAmountFor).map(
    (a, idx) => {
      const paidAmount = round2(a.releasedCredit + a.remainingCredit);
      return {
        itemId: items[idx].itemId,
        selected: items[idx].selected,
        grossTotal: a.grossTotal,
        discountShare: a.discountShare,
        paidAmount,
        netDue: Math.max(0, round2(a.grossTotal - a.discountShare - paidAmount)),
      };
    }
  );

// Thin projection of computeItemBreakdown down to just {itemId, selected,
// netDue} - kept as its own export since it's the shape most call sites
// (dueForSelectedItems, the itemized receipt) actually want.
export const computeItemNetDues = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): INetDueResult[] =>
  computeItemBreakdown(
    items,
    effectiveDiscount,
    billPaidAmount,
    itemPaidAmountFor
  ).map(({ itemId, selected, netDue }) => ({ itemId, selected, netDue }));

// The share of a line that is (or is about to be) in the customer's hands:
// pieces already collected plus pieces selected to leave now, over the pieces
// on the line. 1 for a fully-collected line, 0 for one still untouched.
export const releasedFractionOf = (item: INetDueCartItem): number => {
  const pieces = item.qty ?? 0;
  if (pieces <= 0) return item.selected ? 1 : 0;
  // Pre-partial-quantity callers only carry `selected`.
  if (item.pickedQty === undefined && item.selectedQty === undefined) {
    return item.selected ? 1 : 0;
  }
  const released = (item.pickedQty ?? 0) + (item.selectedQty ?? 0);
  return Math.min(1, Math.max(0, released / pieces));
};

export interface IReleasedDueResult {
  itemId: string;
  // pieces released / pieces on the line
  releasedFraction: number;
  // What the released pieces are worth: their share of the line's
  // gross-minus-discount value.
  releasedValue: number;
  // Still owed for those pieces, once everything already paid against this
  // line is applied to them.
  releasedDue: number;
  // Still owed for the pieces that stay in storage.
  remainingDue: number;
  // This line's REAL discount share (breakdown.discountShare), split by the
  // released/remaining fraction - kept separate from releasedDue/remainingDue
  // above (which ALSO subtract whatever's already been paid) specifically so
  // a receipt can print the true discount without conflating it with money
  // already collected on an earlier visit. Before this field existed, a
  // receipt deriving "discount" as grossPrice - price (releasedDue) got
  // real-discount-plus-prior-payment as one number - so an item paid in
  // full at check-in (no discount at all) printed its ENTIRE price as
  // "Discount" at pickup, making e.g. an alteration's cost look like it had
  // been discounted away instead of already paid for.
  releasedDiscountShare: number;
  remainingDiscountShare: number;
}

/**
 * What a partially-collected line still owes, for the pieces released
 * (already collected plus selected now) and for the pieces staying in
 * storage, once the bill's credit has been used up in pickup order (see
 * allocateCredit). Summed over the bill, releasedDue is exactly
 * (value released so far) - (everything paid), floored at zero - what the
 * backend requires before the garments may leave.
 */
export const computeReleasedDues = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): IReleasedDueResult[] =>
  allocateCredit(items, effectiveDiscount, billPaidAmount, itemPaidAmountFor).map(
    (a, idx) => {
      const releasedDiscountShare = round2(a.discountShare * a.releasedFraction);
      return {
        itemId: items[idx].itemId,
        releasedFraction: a.releasedFraction,
        releasedValue: a.releasedValue,
        releasedDue: Math.max(0, round2(a.releasedValue - a.releasedCredit)),
        remainingDue: Math.max(0, round2(a.remainingValue - a.remainingCredit)),
        releasedDiscountShare,
        // Remainder rather than re-multiplying by (1 - releasedFraction), so
        // the two shares always sum EXACTLY to the line's discount share
        // (same leftover-cent convention as apportion()).
        remainingDiscountShare: round2(a.discountShare - releasedDiscountShare),
      };
    }
  );

const round2 = (value: number): number => Math.round(value * 100) / 100;
